import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  defaultIfEmpty,
  firstValueFrom,
  forkJoin,
  from,
  map,
  of,
  switchMap,
} from 'rxjs';
import { SchedulingService } from '../../home/main/production-reports/scheduling/services/schedule/scheduling.service';
import { MillRun } from '../../home/main/production-reports/mill-reports/models/mill-run.class';
import { Schedule } from '../../home/main/production-reports/scheduling/models/schedule.class';
import { MillRecordsService } from '../../home/main/production-reports/mill-reports/services/mill/mill-records';
import { ProductCodeService } from './product-code.service';

export interface ReportGroup {
  schedule: Schedule;
  mill: MillRun[];
  kiln: any[];
  pkg: any[];
}

@Injectable({
  providedIn: 'root',
})
export class ScheduleRunService {
  private runNumberSubject = new BehaviorSubject<string | null>(null);
  runNumber$ = this.runNumberSubject.asObservable();

  constructor(
    private schedule: SchedulingService,
    private mill: MillRecordsService,
    private productCodeRecs: ProductCodeService,
  ) {}

  setRunNumber(runNumber: string) {
    if (runNumber === '00-000-00') return;
    if (!runNumber) {
      this.runNumberSubject.next('');
      return;
    }
    this.runNumberSubject.next(runNumber);
  }
  get currentRunNumber(): string | null {
    return this.runNumberSubject.getValue();
  }

  getDateCount(recDate: string): number {
    if (!recDate) return 0;
    const today = new Date();
    const prev = new Date(recDate);
    // Normalize both dates to midnight to avoid timezone issues
    today.setHours(0, 0, 0, 0);
    prev.setHours(0, 0, 0, 0);
    const diffMs = Math.abs(today.getTime() - prev.getTime());
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    return diffDays;
  }
  async getRunNumbers(mills: number[], days?: number) {
    // gets run numbers that have been used in mill reports in the last X days
    const unorganizedSchedules = await firstValueFrom(
      this.schedule.fetchRunNumbers(days),
    );
    const filtered = unorganizedSchedules.filter(
      (r: Schedule) => r.run !== '00-000-00',
    );
    const sortedRecs = this.schedule.organizeByRun(filtered);
    return sortedRecs.filter((rec) => mills.includes(rec.mill));
  }

  async getRunNumbersByMill(
    mill: number,
    organize: boolean,
    applyStatus: boolean,
    applyNotes: boolean,
  ) {
    // gets run numbers in current schedule by mill
    const unorganizedSchedules = await this.schedule.grabSchedulesByMill(
      mill,
      applyStatus,
      applyNotes,
    );
    return organize
      ? this.schedule.organizeByRun(unorganizedSchedules)
      : unorganizedSchedules;
  }
  async getRunsNumbersByYear(
    year: string,
    backup: boolean,
    paintGrade: boolean,
  ) {
    // gets run numbers in current schedule by mill
    const unorganizedSchedules =
      await this.schedule.fetchRunNumbersByYear(year);
    const unfilteredRecs = backup
      ? unorganizedSchedules.filter((rec) => rec.comments === 'BACKUP')
      : paintGrade
        ? unorganizedSchedules.filter((rec) => rec.comments === 'PAINT GRADE')
        : unorganizedSchedules;
    return this.schedule.organizeByRun(unfilteredRecs);
  }
  async getCompletedSchedulesByMill(
    plantId: number,
    fetchNotes: boolean,
    year?: number,
  ) {
    const unorganizedSchedules =
      await this.schedule.getCompletedSchedulesByMill(
        plantId,
        fetchNotes,
        year,
      );
    return this.schedule.organizeByRun(unorganizedSchedules);
  }
  getCompletedSchedulesTable() {
    return this.schedule.getCompletedSchedulesTable();
  }
  getSchedulesForLocation(location: any) {
    return this.schedule.getSchedulesForLocation(location);
  }
  fetchMillData(mills: number[]) {
    return this.schedule.fetchMillData(mills);
  }
  getRowClasses(record: any, index?: number, records?: any[]) {
    return this.schedule.getRowClasses(record, index, records);
  }
  getRecordClasses = (record: any, index: number) => {
    return this.schedule.getRecordClasses(record, index);
  };
  fetchAllByScheduleId(schedule: Schedule) {
    return this.schedule.fetchAllByScheduleId(schedule).pipe(
      switchMap((recordsArray) => {
        const enriched = recordsArray.map((record) =>
          from(this.fetchDelays(record.mill)).pipe(
            map((millWithDelays) => ({
              ...record,
              mill: millWithDelays || [],
              kiln: record.kiln,
              pkg: record.pkg,
            })),
          ),
        );

        return forkJoin(enriched);
      }),
    );
  }
  fetchAllByRun(runNumber: string, loadDelays: boolean) {
    return this.schedule.fetchAllByRun(runNumber).pipe(
      switchMap((recordsArray) => {
        const enriched = recordsArray.map((record) => {
          const mill$ = loadDelays
            ? from(this.fetchDelays(record.mill)) // async
            : of(record.mill ?? []);

          return mill$.pipe(
            map((millWithDelays) => ({
              ...record,
              mill: millWithDelays || [],
              kiln: record.kiln || [],
              pkg: record.pkg || [],
            })),
          );
        });

        return forkJoin(enriched);
      }),
    );
  }
  fetchScheduleByRun(runNumber: string) {
    return this.schedule.fetchScheduleByRun(runNumber);
  }
  async loadReportsByRun(runNumber: string, loadDelays: boolean): Promise<any> {
    if (!runNumber || runNumber === '00-000-00') {
      return [];
    }
    try {
      const reports: any[] = await firstValueFrom(
        this.fetchAllByRun(runNumber, loadDelays).pipe(defaultIfEmpty([])), //fetches all data by run & defaults to empty if none exist
      );
      return reports;
    } catch (error) {
      console.error('Error fetching reports:', error);
      return [];
    }
  }
  async fetchDelays(records: MillRun[]) {
    return this.mill.groupRecordsWithDelays(records);
  }
  async openProductCode(record: any, mill: number) {
    this.schedule.productCodeDialog(record, mill);
  }
  addProductcode(group: any) {
    this.productCodeRecs.addProductcode(group);
  }
}
